/**
 * Uniform-grid spatial hash over the unit store.
 *
 * Rebuilt from scratch every tick with a counting sort — no allocation, no
 * per-cell arrays, and iteration order is a pure function of unit index, which
 * keeps neighbour queries deterministic.
 *
 * Two things are layered on top of the plain grid, both of them pure pruning:
 * they change how long a query takes and never which units it returns.
 *
 *   Team masks. Each cell carries a bitfield of the teams standing in it, and
 *   `nearest` takes the mask of the teams its predicate could possibly accept.
 *   A cell with no unit of a wanted team cannot contain an answer, so it is
 *   skipped without touching a single unit. This is what makes twenty thousand
 *   villagers asking "is anything hostile within four tiles" affordable: in a
 *   city at peace the answer is decided by a handful of integer reads.
 *
 *   Block masks. The same bitfield again, one level up over 8×8 blocks of
 *   cells, so a query whose whole search box is free of the wanted team gives
 *   up after two or three reads instead of walking every ring.
 *
 * Neither is a heuristic. `cellMask` is exactly the union of the teams present,
 * so skipping is provably lossless — which matters, because a "nearly right"
 * neighbour query is a determinism bug that only shows up under load.
 */

import { HASH_CELL, MAP_H, MAP_W, MAX_UNITS } from './constants.ts';
import { alliedWith, areHostile } from './types.ts';
import type { UnitStore } from './stores.ts';

const CELLS_X = Math.ceil(MAP_W / HASH_CELL);
const CELLS_Y = Math.ceil(MAP_H / HASH_CELL);
const CELL_COUNT = CELLS_X * CELLS_Y;

/** Cells per coarse block edge. */
const BLOCK = 8;
const BLOCKS_X = Math.ceil(CELLS_X / BLOCK);
const BLOCKS_Y = Math.ceil(CELLS_Y / BLOCK);
const BLOCK_COUNT = BLOCKS_X * BLOCKS_Y;

/** Every team at once — the mask that prunes nothing. */
export const TEAM_ANY = 0xff;

/** The mask for a single team id. */
export function teamMask(team: number): number {
  return 1 << (team & 7);
}

/**
 * What "hostile to me" means as a mask.
 *
 * This used to be "every team except mine", which was the same thing while there
 * were only two that could fight. With four belligerents in two alliances it is
 * not: a mask of everyone-else would sweep cells holding nothing but the
 * player's own ally, and a mask has to be a *superset* of what the predicate
 * accepts to stay lossless, not merely a plausible one.
 *
 * Precomputed off `areHostile` for all eight packed ids, so it is exactly the
 * union of the teams that predicate can return true for — narrower than the old
 * one, which means strictly better pruning and identical answers. A neutral's
 * mask is 0, and `nearest` gives up on it for free.
 */
const HOSTILE_MASKS = (() => {
  const t = new Int32Array(8);
  for (let a = 0; a < 8; a++) {
    let m = 0;
    for (let b = 0; b < 8; b++) if (areHostile(a, b)) m |= 1 << b;
    t[a] = m;
  }
  return t;
})();

export function hostileMask(team: number): number {
  return HOSTILE_MASKS[team & 7];
}

/** Every team on my own side, including me. Not the complement: neutrals are in neither. */
const ALLIED_MASKS = (() => {
  const t = new Int32Array(8);
  for (let a = 0; a < 8; a++) {
    let m = 0;
    for (let b = 0; b < 8; b++) if (alliedWith(a, b)) m |= 1 << b;
    t[a] = m;
  }
  return t;
})();

export function alliedMask(team: number): number {
  return ALLIED_MASKS[team & 7];
}

export class SpatialHash {
  readonly cellsX = CELLS_X;
  readonly cellsY = CELLS_Y;
  /** Start offset of each cell inside `items`, length CELL_COUNT + 1. */
  private readonly starts = new Int32Array(CELL_COUNT + 1);
  private readonly cursor = new Int32Array(CELL_COUNT);
  private readonly items = new Int32Array(MAX_UNITS);
  private readonly cellOf = new Int32Array(MAX_UNITS);
  /** Bitfield of teams present, per cell and per 8×8 block of cells. */
  private readonly cellMask = new Uint8Array(CELL_COUNT);
  private readonly blockMask = new Uint8Array(BLOCK_COUNT);
  /** Live models per team, so a query for a team nobody fields costs nothing. */
  private readonly teamTotals = new Int32Array(8);
  private total = 0;

  private cellIndex(x: number, y: number): number {
    let cx = (x / HASH_CELL) | 0;
    let cy = (y / HASH_CELL) | 0;
    if (cx < 0) cx = 0;
    else if (cx >= CELLS_X) cx = CELLS_X - 1;
    if (cy < 0) cy = 0;
    else if (cy >= CELLS_Y) cy = CELLS_Y - 1;
    return cy * CELLS_X + cx;
  }

  rebuild(units: UnitStore): void {
    this.starts.fill(0);
    this.cellMask.fill(0);
    this.blockMask.fill(0);
    this.teamTotals.fill(0);
    const alive = units.slots.alive;
    const high = units.slots.high;
    let n = 0;

    for (let i = 0; i < high; i++) {
      if (alive[i] === 0) continue;
      const c = this.cellIndex(units.x[i], units.y[i]);
      this.cellOf[i] = c;
      this.starts[c + 1]++;
      const m = 1 << (units.team[i] & 7);
      this.cellMask[c] |= m;
      const bx = ((c % CELLS_X) / BLOCK) | 0;
      const by = (((c / CELLS_X) | 0) / BLOCK) | 0;
      this.blockMask[by * BLOCKS_X + bx] |= m;
      this.teamTotals[units.team[i] & 7]++;
      n++;
    }
    this.total = n;

    for (let c = 0; c < CELL_COUNT; c++) {
      this.starts[c + 1] += this.starts[c];
      this.cursor[c] = this.starts[c];
    }

    for (let i = 0; i < high; i++) {
      if (alive[i] === 0) continue;
      const c = this.cellOf[i];
      this.items[this.cursor[c]++] = i;
    }
  }

  get count(): number {
    return this.total;
  }

  /** How many live models of `team` the hash saw on its last rebuild. */
  countOfTeam(team: number): number {
    return this.teamTotals[team & 7];
  }

  /**
   * Could any unit whose team is in `mask` be inside the search box at all?
   *
   * Two levels of rejection: the global per-team totals, then the coarse block
   * grid. Both are exact, so a `false` here is the same answer the full sweep
   * would have given, arrived at in a few integer reads.
   */
  private boxEmpty(x: number, y: number, radius: number, mask: number): boolean {
    if ((mask & TEAM_ANY) === 0) return true;
    let any = false;
    for (let t = 0; t < 8; t++) {
      if ((mask & (1 << t)) !== 0 && this.teamTotals[t] > 0) {
        any = true;
        break;
      }
    }
    if (!any) return true;

    const cellSpan = HASH_CELL * BLOCK;
    let bx0 = ((x - radius) / cellSpan) | 0;
    let bx1 = ((x + radius) / cellSpan) | 0;
    let by0 = ((y - radius) / cellSpan) | 0;
    let by1 = ((y + radius) / cellSpan) | 0;
    if (bx0 < 0) bx0 = 0;
    if (by0 < 0) by0 = 0;
    if (bx1 >= BLOCKS_X) bx1 = BLOCKS_X - 1;
    if (by1 >= BLOCKS_Y) by1 = BLOCKS_Y - 1;

    for (let by = by0; by <= by1; by++) {
      const row = by * BLOCKS_X;
      for (let bx = bx0; bx <= bx1; bx++) {
        if ((this.blockMask[row + bx] & mask) !== 0) return false;
      }
    }
    return true;
  }

  /**
   * Visit every unit within `radius` tiles of (x, y). The callback receives the
   * unit index and the squared distance.
   */
  query(x: number, y: number, radius: number, units: UnitStore, fn: (i: number, d2: number) => void): void {
    const r2 = radius * radius;
    const minCx = Math.max(0, (((x - radius) / HASH_CELL) | 0));
    const maxCx = Math.min(CELLS_X - 1, (((x + radius) / HASH_CELL) | 0));
    const minCy = Math.max(0, (((y - radius) / HASH_CELL) | 0));
    const maxCy = Math.min(CELLS_Y - 1, (((y + radius) / HASH_CELL) | 0));

    for (let cy = minCy; cy <= maxCy; cy++) {
      const rowBase = cy * CELLS_X;
      for (let cx = minCx; cx <= maxCx; cx++) {
        const c = rowBase + cx;
        const s = this.starts[c];
        const e = this.starts[c + 1];
        for (let k = s; k < e; k++) {
          const i = this.items[k];
          const dx = units.x[i] - x;
          const dy = units.y[i] - y;
          const d2 = dx * dx + dy * dy;
          if (d2 <= r2) fn(i, d2);
        }
      }
    }
  }

  /**
   * The same sweep as `query`, but handing the caller a whole cell at a time
   * instead of one unit at a time.
   *
   * Crowd separation is the highest-frequency query in the game — every ground
   * unit, every tick — and at that rate the per-candidate callback is itself a
   * measurable share of the frame: two dozen closure invocations per unit,
   * where four would do. Handing over the run of indices lets the caller keep
   * its accumulator in registers across the whole cell.
   *
   * Iteration order is identical to `query`'s, which matters: separation sums
   * floating-point pushes, and a different summation order is a different
   * number.
   */
  forEachCell(
    x: number,
    y: number,
    radius: number,
    fn: (items: Int32Array, start: number, end: number) => void,
  ): void {
    const minCx = Math.max(0, (((x - radius) / HASH_CELL) | 0));
    const maxCx = Math.min(CELLS_X - 1, (((x + radius) / HASH_CELL) | 0));
    const minCy = Math.max(0, (((y - radius) / HASH_CELL) | 0));
    const maxCy = Math.min(CELLS_Y - 1, (((y + radius) / HASH_CELL) | 0));

    for (let cy = minCy; cy <= maxCy; cy++) {
      const rowBase = cy * CELLS_X;
      for (let cx = minCx; cx <= maxCx; cx++) {
        const c = rowBase + cx;
        const s = this.starts[c];
        const e = this.starts[c + 1];
        if (s !== e) fn(this.items, s, e);
      }
    }
  }

  /**
   * Nearest live unit matching a predicate. Searches outward one ring of cells
   * at a time and stops as soon as a hit cannot be beaten by the next ring.
   *
   * `mask` must be a superset of the teams `accept` can return true for. It is
   * only ever used to skip cells that cannot hold a match, so passing
   * `TEAM_ANY` is always correct and merely slower.
   */
  nearest(
    x: number,
    y: number,
    maxRadius: number,
    units: UnitStore,
    accept: (i: number) => boolean,
    mask: number = TEAM_ANY,
  ): number {
    if (mask !== TEAM_ANY && this.boxEmpty(x, y, maxRadius, mask)) return -1;

    const cx0 = Math.max(0, Math.min(CELLS_X - 1, (x / HASH_CELL) | 0));
    const cy0 = Math.max(0, Math.min(CELLS_Y - 1, (y / HASH_CELL) | 0));
    const maxRing = Math.ceil(maxRadius / HASH_CELL);
    let best = -1;
    let bestD2 = maxRadius * maxRadius;

    for (let ring = 0; ring <= maxRing; ring++) {
      const xLo = cx0 - ring;
      const xHi = cx0 + ring;
      const yLo = cy0 - ring;
      const yHi = cy0 + ring;
      for (let cy = yLo; cy <= yHi; cy++) {
        if (cy < 0 || cy >= CELLS_Y) continue;
        const onYEdge = cy === yLo || cy === yHi;
        for (let cx = xLo; cx <= xHi; cx++) {
          if (cx < 0 || cx >= CELLS_X) continue;
          if (!onYEdge && cx !== xLo && cx !== xHi) continue;
          const c = cy * CELLS_X + cx;
          if ((this.cellMask[c] & mask) === 0) continue;
          const s = this.starts[c];
          const e = this.starts[c + 1];
          for (let k = s; k < e; k++) {
            const i = this.items[k];
            const dx = units.x[i] - x;
            const dy = units.y[i] - y;
            const d2 = dx * dx + dy * dy;
            if (d2 < bestD2 && accept(i)) {
              bestD2 = d2;
              best = i;
            }
          }
        }
      }
      // A hit inside the already-searched square cannot be beaten by outer rings.
      if (best >= 0 && bestD2 <= (ring * HASH_CELL) * (ring * HASH_CELL)) break;
    }
    return best;
  }
}
